/**
 * ClockPopover — header clock button + popover showing local, PST, and up to
 * 5 user-configured world timezones.
 *
 * Timezone selections are persisted in localStorage under the key
 * "clockPopover:timezones" as a JSON string array of IANA tz identifiers.
 * Up to 2 timezones can be pinned for digital display in the navbar button.
 * Local time is always pinned (and cannot be unpinned); PT is pinned by
 * default so users who never open the popover still see both digital clocks.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useI18nText } from '@/lib/utils/i18nText';
import Tooltip from '@/components/Tooltip';
import { GgcCheckbox } from '@/components/ui/ggc/checkbox';

/* ── Constants ─────────────────────────────────────────────────── */

const STORAGE_KEY        = 'clockPopover:timezones';
const DIGITAL_TZ_LIST_KEY = 'clockPopover:digitalTzList';
const MAX_EXTRA          = 5;
const MAX_DIGITAL        = 2;

/** Digital-pin key for local time — always pinned, cannot be unpinned */
const LOCAL_TZ_KEY = 'local';

/** Digital clocks shown before the user configures anything */
const DEFAULT_DIGITAL_TZ_LIST: string[] = [LOCAL_TZ_KEY, 'America/Los_Angeles'];

/** Fixed timezone always shown below Local */
const FIXED_TZ = [
  { tz: 'America/Los_Angeles', label: 'PT', city: 'Los Angeles' },
] as const;

/** 6:00–20:00 is considered daytime */
const isDaytime = (hour24: number) => hour24 >= 6 && hour24 < 20;

/** Curated list of major world timezones users can add */
export const TIMEZONE_OPTIONS: { value: string; label: string; city: string }[] = [
  { value: 'America/New_York',   label: 'EST / EDT',   city: 'New York'   },
  { value: 'America/Chicago',    label: 'CST / CDT',   city: 'Chicago'    },
  { value: 'America/Denver',     label: 'MST / MDT',   city: 'Denver'     },
  { value: 'America/Sao_Paulo',  label: 'BRT',         city: 'São Paulo'  },
  { value: 'Europe/London',      label: 'GMT / BST',   city: 'London'     },
  { value: 'Europe/Paris',       label: 'CET / CEST',  city: 'Paris'      },
  { value: 'Europe/Berlin',      label: 'CET / CEST',  city: 'Berlin'     },
  { value: 'Europe/Moscow',      label: 'MSK',         city: 'Moscow'     },
  { value: 'Asia/Dubai',         label: 'GST',         city: 'Dubai'      },
  { value: 'Asia/Karachi',       label: 'PKT',         city: 'Karachi'    },
  { value: 'Asia/Kolkata',       label: 'IST',         city: 'India'      },
  { value: 'Asia/Dhaka',         label: 'BST',         city: 'Dhaka'      },
  { value: 'Asia/Bangkok',       label: 'ICT',         city: 'Bangkok'    },
  { value: 'Asia/Singapore',     label: 'SGT',         city: 'Singapore'  },
  { value: 'Asia/Shanghai',      label: 'CST',         city: 'Shanghai'   },
  { value: 'Asia/Seoul',         label: 'KST',         city: 'Seoul'      },
  { value: 'Asia/Tokyo',         label: 'JST',         city: 'Tokyo'      },
  { value: 'Australia/Sydney',   label: 'AEST / AEDT', city: 'Sydney'     },
  { value: 'Pacific/Auckland',   label: 'NZST / NZDT', city: 'Auckland'   },
  { value: 'Pacific/Honolulu',   label: 'HST',         city: 'Honolulu'   },
];

/* ── Helpers ───────────────────────────────────────────────────── */

interface TimeParts {
  h12: number;  // 0–11
  m: number;
  s: number;
  hour24: number;
  timeStr: string;
  dateStr: string;
  /** Short timezone name, e.g. "PDT", "KST", "GMT+9" */
  offsetStr: string;
  /** Numeric GMT offset, e.g. "GMT-7", "GMT+9" (empty when same as offsetStr) */
  gmtOffsetStr: string;
}

/** Returns "GMT±N" offset string using shortOffset (e.g. "GMT-7", "GMT+9"). */
function getGmtOffset(date: Date, tz?: string): string {
  const opts: Intl.DateTimeFormatOptions = { timeZoneName: 'shortOffset' };
  if (tz) opts.timeZone = tz;
  const parts = new Intl.DateTimeFormat('en-US', opts).formatToParts(date);
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
}

function getLocalTimeParts(date: Date): TimeParts {
  const hour24 = date.getHours();
  const m = date.getMinutes();
  const s = date.getSeconds();
  const pad = (n: number) => String(n).padStart(2, '0');
  const offsetParts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(date);
  const offsetStr = offsetParts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  const gmtRaw = getGmtOffset(date);
  const dateStr = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', weekday: 'short', year: 'numeric' }).format(date);
  return {
    h12: hour24 % 12, m, s, hour24,
    timeStr: `${pad(hour24)}:${pad(m)}:${pad(s)}`,
    dateStr,
    offsetStr,
    // Avoid showing "GMT+9 (GMT+9)" — omit gmtOffsetStr when offsetStr already contains the offset number
    gmtOffsetStr: offsetStr === gmtRaw ? '' : gmtRaw,
  };
}

function getTzTimeParts(date: Date, tz: string): TimeParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const get = (type: string) => parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);
  const hour24 = get('hour');
  const m = get('minute');
  const s = get('second');
  const pad = (n: number) => String(n).padStart(2, '0');
  const offsetParts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(date);
  const offsetStr = offsetParts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  const gmtRaw = getGmtOffset(date, tz);
  const dateStr = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, month: 'short', day: 'numeric', weekday: 'short', year: 'numeric',
  }).format(date);
  return {
    h12: hour24 % 12, m, s, hour24,
    timeStr: `${pad(hour24)}:${pad(m)}:${pad(s)}`,
    dateStr,
    offsetStr,
    gmtOffsetStr: offsetStr === gmtRaw ? '' : gmtRaw,
  };
}

function loadSavedZones(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string').slice(0, MAX_EXTRA);
  } catch {
    return [];
  }
}

/**
 * Reads the pinned digital clocks, falling back to the default (local + PT)
 * when nothing was ever saved. Local is force-pinned first for saved lists too,
 * so previously stored selections stay consistent with the locked local row.
 */
function loadDigitalTzList(): string[] {
  try {
    const raw = localStorage.getItem(DIGITAL_TZ_LIST_KEY);
    if (!raw) return DEFAULT_DIGITAL_TZ_LIST;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return DEFAULT_DIGITAL_TZ_LIST;
    const saved = parsed.filter((v): v is string => typeof v === 'string');
    const withLocal = [LOCAL_TZ_KEY, ...saved.filter((tz) => tz !== LOCAL_TZ_KEY)];
    return withLocal.slice(0, MAX_DIGITAL);
  } catch {
    return DEFAULT_DIGITAL_TZ_LIST;
  }
}

/* ── Analog Clock SVG ──────────────────────────────────────────── */

/**
 * Analog clock icon with day/night theming.
 * - daytime  (6–20 h): warm cream background, amber/brown hands
 * - nighttime (20–6 h): dark navy background, slate/white hands, blue second
 */
function AnalogClockIcon({
  h12, m, s, hour24, size = 22,
}: {
  h12: number;
  m: number;
  s: number;
  hour24: number;
  size?: number;
}) {
  const day = isDaytime(hour24);

  // Theme tokens
  const bgFill    = day ? '#FFFFFF' : '#0F172A';
  const faceStroke = day ? '#64748B' : '#475569';
  const tickColor = day ? '#64748B' : '#64748B';
  const hourColor  = day ? '#0F172A' : '#E2E8F0';
  const minColor   = day ? '#0F172A' : '#CBD5E1';
  const secColor   = day ? '#64748B77' : '#60A5FA';
  const dotColor   = day ? '#64748B' : '#E2E8F0';

  // Degrees from 12 o'clock, clockwise
  const secDeg  = s * 6;
  const minDeg  = m * 6  + s * 0.1;
  const hourDeg = h12 * 30 + m * 0.5;

  const cx = size / 2;
  const r  = size / 2 - 1;

  const hourLen = r * 0.52;
  const minLen  = r * 0.72;
  const secLen  = r * 0.80;

  const handEnd = (deg: number, len: number) => {
    const rad = (deg - 90) * (Math.PI / 180);
    return { x2: cx + Math.cos(rad) * len, y2: cx + Math.sin(rad) * len };
  };

  const hourHand = handEnd(hourDeg, hourLen);
  const minHand  = handEnd(minDeg,  minLen);
  const secHand  = handEnd(secDeg,  secLen);

  const ticks = [0, 90, 180, 270].map((deg) => {
    const rad = (deg - 90) * (Math.PI / 180);
    return {
      x1: cx + Math.cos(rad) * r * 0.82,
      y1: cx + Math.sin(rad) * r * 0.82,
      x2: cx + Math.cos(rad) * r * 0.97,
      y2: cx + Math.sin(rad) * r * 0.97,
    };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" aria-hidden>
      {/* Background fill */}
      <circle cx={cx} cy={cx} r={r} fill={bgFill} />
      {/* Face ring */}
      <circle cx={cx} cy={cx} r={r} stroke={faceStroke} strokeWidth="1.2" />
      {/* Tick marks */}
      {ticks.map((t, i) => (
        <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
          stroke={tickColor} strokeWidth="1.1" strokeLinecap="round" />
      ))}
      {/* Hour hand */}
      <line x1={cx} y1={cx} x2={hourHand.x2} y2={hourHand.y2}
        stroke={hourColor} strokeWidth="1.8" strokeLinecap="round" />
      {/* Minute hand */}
      <line x1={cx} y1={cx} x2={minHand.x2} y2={minHand.y2}
        stroke={minColor} strokeWidth="1.3" strokeLinecap="round" />
      {/* Second hand */}
      <line x1={cx} y1={cx} x2={secHand.x2} y2={secHand.y2}
        stroke={secColor} strokeWidth="0.9" strokeLinecap="round" />
      {/* Center dot */}
      <circle cx={cx} cy={cx} r="1.2" fill={dotColor} />
    </svg>
  );
}

/* ── ClockRow ──────────────────────────────────────────────────── */

function ClockRow({
  label,
  parts,
  onRemove,
  tzKey,
  digitalTzList,
  onDigitalToggle,
}: {
  label: React.ReactNode;
  parts: TimeParts;
  onRemove?: () => void;
  /** Identifier used to look up digital-pin state ('local' or IANA tz string) */
  tzKey: string;
  digitalTzList: string[];
  onDigitalToggle: (tz: string) => void;
}) {
  const { t } = useI18nText();
  // Local time is permanently pinned — render it checked and locked
  const isLocked   = tzKey === LOCAL_TZ_KEY;
  const isChecked  = isLocked || digitalTzList.includes(tzKey);
  const isDisabled = isLocked || (!isChecked && digitalTzList.length >= MAX_DIGITAL);

  return (
    <div className="flex items-stretch gap-2 py-2 px-3 rounded-ggc-sm hover:bg-ggc-bg-base group/row">
      {/* Digital-pin checkbox with tooltip */}
      <div className="shrink-0 flex items-center">
        <Tooltip
          content={
            isLocked
              ? <span>{t('Local time is always shown', '현재 위치 시간은 항상 표시됩니다')}</span>
              : isDisabled
                ? <span>{t(`Max ${MAX_DIGITAL} digital clocks — uncheck another one first`, `디지털 시계는 최대 ${MAX_DIGITAL}개입니다 — 다른 항목을 먼저 해제하세요`)}</span>
                : <span>{isChecked ? t('Remove from digital display', '디지털 시계 표시 해제') : t('Show as digital clock', '디지털 시계로 표시')}</span>
          }
          placement="right"
        >
          <span className="inline-flex">
            <GgcCheckbox
              checked={isChecked}
              disabled={isDisabled}
              onChange={() => onDigitalToggle(tzKey)}
            />
          </span>
        </Tooltip>
      </div>
      {/* Text info — grows to fill available width */}
      <div className="flex flex-col justify-center min-w-0 flex-1">
        <div className="flex items-center gap-1 min-w-0 mb-0.5">
          <span className="ggc-text-body-bold text-ggc-primary truncate">{label}</span>
          {(parts.offsetStr || parts.gmtOffsetStr) && (
            <span className="ggc-text-caption-m text-ggc-primary opacity-60 shrink-0">
              {parts.offsetStr}
              {parts.gmtOffsetStr && (
                <span className="ml-0.5 opacity-80">({parts.gmtOffsetStr})</span>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <i
            className={`fa ${isDaytime(parts.hour24) ? 'fa-sun-o' : 'fa-moon-o'} text-sm`}
            style={{ color: isDaytime(parts.hour24) ? '#F59E0B' : '#60A5FA' }}
            aria-hidden
          />
          <span className="font-mono font-semibold text-[18px] tabular-nums leading-tight text-ggc-text-primary">
            {parts.timeStr}
          </span>
        </div>
        <span className="ggc-text-caption-s text-ggc-disabled mt-0.5">{parts.dateStr}</span>
      </div>
      {/* Analog clock — always vertically centered regardless of row height */}
      <div className="shrink-0 flex items-center">
        <AnalogClockIcon h12={parts.h12} m={parts.m} s={parts.s} hour24={parts.hour24} size={36} />
      </div>
      {/* Remove button placeholder — always same width (w-4) to keep clock aligned */}
      <div className="shrink-0 w-4 flex items-center">
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="opacity-0 group-hover/row:opacity-100 transition-opacity text-ggc-disabled hover:text-ggc-danger"
            title="Remove"
          >
            <i className="fa fa-times text-xs" aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Main Component ────────────────────────────────────────────── */

export function ClockPopover() {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [extraZones, setExtraZones] = useState<string[]>(loadSavedZones);
  const [showAdd, setShowAdd] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  /** IANA tz keys of pinned digital-display rows (max 2; 'local' for local time) */
  const [digitalTzList, setDigitalTzList] = useState<string[]>(loadDigitalTzList);
  const popoverRef   = useRef<HTMLDivElement>(null);
  const addSearchRef = useRef<HTMLInputElement>(null);

  // Tick every second — always running so the icon hands move even when closed
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1_000);
    return () => clearInterval(id);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowAdd(false);
        setAddSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (showAdd) setTimeout(() => addSearchRef.current?.focus(), 50);
  }, [showAdd]);

  const toggleDigitalTz = useCallback((tz: string) => {
    // Local time cannot be unpinned
    if (tz === LOCAL_TZ_KEY) return;
    setDigitalTzList((prev) => {
      let next: string[];
      if (prev.includes(tz)) {
        next = prev.filter((z) => z !== tz);
      } else if (prev.length < MAX_DIGITAL) {
        next = [...prev, tz];
      } else {
        return prev;
      }
      localStorage.setItem(DIGITAL_TZ_LIST_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const removeZone = useCallback((tz: string) => {
    setExtraZones((prev) => {
      const next = prev.filter((z) => z !== tz);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
    // Also remove from digital list if pinned
    setDigitalTzList((prev) => {
      if (!prev.includes(tz)) return prev;
      const next = prev.filter((z) => z !== tz);
      localStorage.setItem(DIGITAL_TZ_LIST_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const addZone = useCallback((tz: string) => {
    setExtraZones((prev) => {
      if (prev.includes(tz) || prev.length >= MAX_EXTRA) return prev;
      const next = [...prev, tz];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
    setShowAdd(false);
    setAddSearch('');
  }, []);

  // Derive time parts per timezone
  const localParts = getLocalTimeParts(now);
  const fixedParts = FIXED_TZ.map((f) => ({ ...f, parts: getTzTimeParts(now, f.tz) }));

  // Compute display parts for every pinned digital timezone
  const digitalDisplayList = digitalTzList.map((tz) => {
    const parts = tz === LOCAL_TZ_KEY ? localParts : (() => {
      try { return getTzTimeParts(now, tz); } catch { return null; }
    })();
    if (!parts) return null;
    return { parts, offsetLabel: parts.offsetStr || parts.gmtOffsetStr };
  }).filter((d): d is { parts: TimeParts; offsetLabel: string } => d !== null);

  const filteredOptions = TIMEZONE_OPTIONS.filter((opt) => {
    if (extraZones.includes(opt.value)) return false;
    if (!addSearch) return true;
    const q = addSearch.toLowerCase();
    return (
      opt.city.toLowerCase().includes(q) ||
      opt.label.toLowerCase().includes(q) ||
      opt.value.toLowerCase().includes(q)
    );
  });

  // Header analog icon always reflects local time
  const localH12 = now.getHours() % 12;
  const localM   = now.getMinutes();
  const localS   = now.getSeconds();
  const localH24 = now.getHours();

  return (
    <div ref={popoverRef} className="navbar-clock-anchor relative flex items-center">
      <button
        type="button"
        className="navbar-icon-btn !text-white"
        onClick={() => { setOpen((v) => !v); setShowAdd(false); setAddSearch(''); }}
        title={t('World Clock', '세계 시계')}
      >
        {digitalDisplayList.length > 0 ? (
          <div className="flex items-start leading-none">
            {digitalDisplayList.map((d, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <div className="w-px self-stretch bg-white opacity-30 mx-[10px]" />
                )}
                <div className="flex flex-col gap-[2px]">
                  <div className="flex items-baseline gap-[3px]">
                    <i
                      className={`fa ${isDaytime(d.parts.hour24) ? 'fa-sun-o' : 'fa-moon-o'} text-[10px] self-center`}
                      style={{ color: isDaytime(d.parts.hour24) ? '#FCD34D' : '#93C5FD' }}
                      aria-hidden
                    />
                    <span className="font-mono font-semibold tabular-nums text-[18px]">
                      {d.parts.timeStr.slice(0, 5)}
                    </span>
                    {d.offsetLabel && (
                      <span className="font-mono text-[10px] opacity-80">
                        {d.offsetLabel}
                      </span>
                    )}
                  </div>
                  <span className="font-mono text-[9px] opacity-70 tracking-tight">
                    {d.parts.dateStr}
                  </span>
                </div>
              </React.Fragment>
            ))}
            <div className="w-px self-stretch bg-white/25 ml-[10px]" />
          </div>
        ) : (
          <AnalogClockIcon h12={localH12} m={localM} s={localS} hour24={localH24} size={22} />
        )}
      </button>

      {open && (
        <div className="navbar-clock-popover absolute right-0 top-full mt-1 w-[290px] rounded-ggc-md border border-ggc-border-gray bg-white shadow-ggc-lg z-[1100]">
          {/* Fixed rows: Local + PT */}
          <div className="px-1 pt-2 pb-1">
            <ClockRow
              tzKey={LOCAL_TZ_KEY}
              label={t('Local', '현재 위치')}
              parts={localParts}
              digitalTzList={digitalTzList}
              onDigitalToggle={toggleDigitalTz}
            />
            {fixedParts.map((f) => (
              <ClockRow
                key={f.tz}
                tzKey={f.tz}
                label={`${f.city} (${f.label})`}
                parts={f.parts}
                digitalTzList={digitalTzList}
                onDigitalToggle={toggleDigitalTz}
              />
            ))}
          </div>

          {/* Extra zones */}
          {extraZones.length > 0 && (
            <>
              <div className="mx-3 border-t border-ggc-border-gray" />
              <div className="px-1 py-1">
                {extraZones.map((tz) => {
                  const opt = TIMEZONE_OPTIONS.find((o) => o.value === tz);
                  let parts: TimeParts;
                  try {
                    parts = getTzTimeParts(now, tz);
                  } catch {
                    return null;
                  }
                  const label = opt ? `${opt.city} (${opt.label})` : tz;
                  return (
                    <ClockRow
                      key={tz}
                      tzKey={tz}
                      label={label}
                      parts={parts}
                      onRemove={() => removeZone(tz)}
                      digitalTzList={digitalTzList}
                      onDigitalToggle={toggleDigitalTz}
                    />
                  );
                })}
              </div>
            </>
          )}

          {/* Add timezone */}
          <div className="mx-3 border-t border-ggc-border-gray" />
          <div className="px-3 pt-2 pb-1">
            {extraZones.length < MAX_EXTRA ? (
              showAdd ? (
                <div className="flex flex-col gap-1.5">
                  <input
                    ref={addSearchRef}
                    type="text"
                    value={addSearch}
                    onChange={(e) => setAddSearch(e.target.value)}
                    placeholder={t('Search city or timezone…', '도시 또는 타임존 검색…')}
                    className="w-full px-2 py-1.5 text-xs border border-ggc-border-gray rounded-ggc-sm outline-none focus:border-ggc-main"
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') { setShowAdd(false); setAddSearch(''); }
                    }}
                  />
                  <div className="max-h-[180px] overflow-y-auto rounded-ggc-sm border border-ggc-border-gray">
                    {filteredOptions.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-ggc-disabled">
                        {t('No results', '결과 없음')}
                      </div>
                    ) : (
                      filteredOptions.map((opt) => {
                        const gmtOffset = getGmtOffset(now, opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-xs text-ggc-text-primary hover:bg-ggc-bg-base text-left"
                            onClick={() => addZone(opt.value)}
                          >
                            <span className="font-medium">{opt.city}</span>
                            <span className="text-ggc-disabled shrink-0">
                              {opt.label}
                              {gmtOffset && (
                                <span className="ml-1 opacity-70">({gmtOffset})</span>
                              )}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                  <button
                    type="button"
                    className="text-xs text-ggc-disabled hover:text-ggc-text-gray"
                    onClick={() => { setShowAdd(false); setAddSearch(''); }}
                  >
                    {t('Cancel', '취소')}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-xs text-ggc-disabled hover:text-ggc-cta-blue transition-colors w-full"
                  onClick={() => setShowAdd(true)}
                >
                  <i className="fa fa-plus" aria-hidden />
                  {t('Add timezone', '타임존 추가')}
                  <span className="ml-auto opacity-60">{extraZones.length}/{MAX_EXTRA}</span>
                </button>
              )
            ) : (
              <p className="text-xs text-ggc-disabled text-center">
                {t('Maximum 5 timezones added', '최대 5개 타임존 추가됨')}
              </p>
            )}
          </div>
          {/* Timezone converter link */}
          <div className="mx-3 border-t border-ggc-border-gray mt-1" />
          <div className="px-3 py-2">
            <a
              href="https://www.timeanddate.com/worldclock/converter.html"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-ggc-disabled hover:text-ggc-cta-blue transition-colors w-full"
            >
              <i className="fa fa-calculator" aria-hidden />
              {t('Timezone Converter', '타임존 변환하기')}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
